/**
 * Dashboard-only constants and type extensions for court availability templates.
 * These live outside the synced DTO file so dto:sync never overwrites them.
 */
import {
  CreateCourtFormAvailabilityInputType as BaseInputType,
  CourtAvailabilityTemplateRuleDto,
} from './court-availability-template.dto';

export const SLOTS_PER_HOUR = 4;
export const MINUTES_PER_SLOT = 15;

/** Slot index of midnight-to-midnight. A slider value is always in [0, this]. */
export const SLOTS_PER_DAY = 24 * SLOTS_PER_HOUR;

/**
 * Dashboard-only extension — adds `priceRaw` (the in-progress text of the price
 * field, which never leaves the form) to configurations.
 *
 * `capacity` used to be patched on here too. It is now declared in the synced
 * DTO itself, because it does NOT stay in the browser: it rides the template
 * round trip through the API's `jsonb` column and back into this form. A field
 * the server persists belongs in the shape the server publishes — patching it
 * on locally is what kept it invisible to the API DTO, the entity type and the
 * public Swagger schema for months.
 */
export type CreateCourtFormAvailabilityInputType = Omit<BaseInputType, 'configurations'> & {
  configurations: (BaseInputType['configurations'][number] & { priceRaw?: string })[];
};

/**
 * The availability-template rule encoding, and why it lives HERE.
 *
 * The form holds each block as a pair of slot indices (`min`/`max`) on a
 * `SLOTS_PER_HOUR` grid. The persisted template stores them as the wall-clock
 * time of day on a fixed reference date (2024-01-01), because the entity's
 * columns are `Date`s. The template is the form's memory: it is what the
 * modal re-seeds from, and it is not read by the API for anything else.
 *
 * These two functions used to live in the SYNCED DTO file next door with the
 * conversion hardcoded as `/2` and `* 30` — correct only while
 * `SLOTS_PER_HOUR` was 2. `505148da9` (2026-07-02) moved the slider to
 * 15-minute steps (2 -> 4, 30 -> 15) and did not follow, so encode and decode
 * were on different grids for six weeks:
 *
 *   - Every template written BEFORE that commit stores the true wall-clock
 *     time, and has been read back at HALF of it ever since (a court open
 *     08:00–22:00 renders as 04:00–11:00 in the modal).
 *   - Every template written AFTER it stores DOUBLE the true time, so any
 *     block reaching 12:00 overflows the reference day: `max = 88` (22:00)
 *     encodes as `new Date(2024, 0, 1, 44, 0)` = Jan 2 20:00, which decodes
 *     back to 10:00 — and drags `daysOfWeek` forward by a day with it.
 *
 * Because `batchUpsertAvailabilitiesCompact` DELETES and rebuilds every
 * availability in the submitted window, an operator who opens the modal to
 * change one price and saves what they were shown erases the rest of the day
 * for a year. Hence: the conversion is derived from the two constants above
 * so the pair can never drift again, and it lives next to them rather than in
 * a file `npm run dto:sync --force` can overwrite.
 */
const TEMPLATE_REFERENCE_YEAR = 2024;
const TEMPLATE_REFERENCE_MONTH = 0; // January 2024 — the 1st is a Monday.
const TEMPLATE_REFERENCE_DATE = 1;

/** Slot index -> `{ hour, minute }` on the `SLOTS_PER_HOUR` grid. */
export function slotIndexToHourMinute(slotIndex: number): {
  hour: number;
  minute: number;
} {
  return {
    hour: Math.floor(slotIndex / SLOTS_PER_HOUR),
    minute: (slotIndex % SLOTS_PER_HOUR) * MINUTES_PER_SLOT,
  };
}

/**
 * Wall-clock time on the reference date -> slot index.
 *
 * The day offset is deliberately included rather than ignored: a block ending
 * at midnight legitimately encodes as Jan 2 00:00 (`SLOTS_PER_DAY`), and a
 * rule written by the pre-fix encoder lands further into January still, which
 * is what makes `isMisencodedTemplateRule` below exact rather than a guess.
 */
const MS_PER_DAY = 24 * 60 * 60 * 1000;

function dateToSlotIndex(value: Date | string): number {
  const date = new Date(value);
  const localMidnight = new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
  );
  const reference = new Date(
    TEMPLATE_REFERENCE_YEAR,
    TEMPLATE_REFERENCE_MONTH,
    TEMPLATE_REFERENCE_DATE,
  );
  // Measured against the reference instant, not `getDate()`, so a stored value
  // that lands in another month (or before the reference) yields a real offset
  // instead of a day-of-month read as one. `Math.round` absorbs the hour a DST
  // boundary can put between two local midnights.
  const dayOffset = Math.round(
    (localMidnight.getTime() - reference.getTime()) / MS_PER_DAY,
  );
  return (
    (dayOffset * 24 + date.getHours()) * SLOTS_PER_HOUR +
    date.getMinutes() / MINUTES_PER_SLOT
  );
}

const clampToDay = (slotIndex: number) =>
  Math.max(0, Math.min(SLOTS_PER_DAY, slotIndex));

export function transformInputToRule(
  input: CreateCourtFormAvailabilityInputType,
): CourtAvailabilityTemplateRuleDto {
  const start = slotIndexToHourMinute(input.min);
  const end = slotIndexToHourMinute(input.max);
  return {
    id: input.id,
    // NOTE: availabilityTemplateId is set to the rule id here; callers must override with the actual template id
    availabilityTemplateId: input.id,
    startTime: new Date(
      TEMPLATE_REFERENCE_YEAR,
      TEMPLATE_REFERENCE_MONTH,
      TEMPLATE_REFERENCE_DATE,
      start.hour,
      start.minute,
      0,
    ),
    // `max === SLOTS_PER_DAY` means "open until midnight" and encodes as hour
    // 24, i.e. Jan 2 00:00. `dateToSlotIndex` reads the day offset back, so
    // that survives the round trip instead of collapsing to 0 the way it did
    // when only `getHours()` was consulted.
    endTime: new Date(
      TEMPLATE_REFERENCE_YEAR,
      TEMPLATE_REFERENCE_MONTH,
      TEMPLATE_REFERENCE_DATE,
      end.hour,
      end.minute,
      0,
    ),
    slotDurationInMs: input.slotDurationInMs,
    daysOfWeek: input.daysOfWeek.map(
      (d) =>
        new Date(
          TEMPLATE_REFERENCE_YEAR,
          TEMPLATE_REFERENCE_MONTH,
          // January 2024 starts on a Monday, so day-of-week N is the Nth of
          // the month; Sunday (0) is the 7th.
          d === 0 ? 7 : d,
          // Only `getDay()` is ever read back from these, and an hour of 24
          // would roll the date — and therefore the weekday — forward. Clamp
          // so the encoded weekday cannot depend on the block's start time.
          Math.min(start.hour, 23),
          start.minute,
          0,
        ),
    ),
    configurations: input.configurations,
  };
}

export function transformRuleToInput(
  input: CourtAvailabilityTemplateRuleDto,
): CreateCourtFormAvailabilityInputType {
  return {
    id: input.id,
    daysOfWeek: input.daysOfWeek.map((d) => new Date(d).getDay()),
    // Clamped so a rule written by the pre-fix encoder cannot hand the slider
    // a value outside its own range. `isMisencodedTemplateRule` is how the UI
    // learns that clamping happened; silently seeding a clamped block would
    // put a wrong schedule in front of an operator whose next save rebuilds a
    // year of availabilities from it.
    min: clampToDay(dateToSlotIndex(input.startTime)),
    max: clampToDay(dateToSlotIndex(input.endTime)),
    slotDurationInMs: input.slotDurationInMs,
    configurations: input.configurations,
  };
}

/**
 * True when a stored rule cannot have been written by `transformInputToRule`.
 *
 * The correct encoder only ever produces times on the reference date, plus
 * exactly Jan 2 00:00 for a block that runs to midnight — so every slot index
 * it emits is within `[0, SLOTS_PER_DAY]`. The pre-fix encoder doubled the
 * hour, so any block reaching 12:00 encodes past that ceiling.
 *
 * Two limits, both stated rather than papered over:
 *
 *  - A mis-encoded block that ends BEFORE 12:00 stays inside the ceiling (an
 *    intended 08:00–10:00 stored as 16:00–20:00 is indistinguishable from a
 *    real 16:00–20:00 block), and the template carries no creation timestamp
 *    to separate them. Those read back at double their real hours with no
 *    warning.
 *  - The stored value is a wall-clock time written by one browser and read by
 *    another, so a template saved in a different timezone can cross the
 *    reference date on its own and trip this. That is not a harmful false
 *    positive: the decoded hours really are shifted in that case, and the
 *    warning this drives says exactly what to do about it — check the hours
 *    before saving. The timezone skew itself is older than this encoding and
 *    is not addressed here.
 */
export function isMisencodedTemplateRule(
  rule: Pick<CourtAvailabilityTemplateRuleDto, 'startTime' | 'endTime'>,
): boolean {
  return (
    dateToSlotIndex(rule.startTime) > SLOTS_PER_DAY ||
    dateToSlotIndex(rule.endTime) > SLOTS_PER_DAY
  );
}
